import { useState, useEffect, useRef } from 'react'
import { Table, Card, Tag, Button, Space, Modal, Form, Input, Select, InputNumber, message, Popconfirm } from 'antd'
import { PlusOutlined, CheckOutlined, CloseOutlined, DeleteOutlined, UploadOutlined, DownloadOutlined, FileOutlined } from '@ant-design/icons'
import type { UploadFile } from 'antd'
import { purchaseInvoiceApi } from '../services/api'
import axios from 'axios'

const statusMap: Record<number, { label: string; color: string }> = {
  1: { label: '待审核', color: 'orange' },
  2: { label: '已通过', color: 'green' },
  3: { label: '已拒绝', color: 'red' }
}

export default function PurchaseInvoices() {
  const [loading, setLoading] = useState(false)
  const [list, setList] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<number | undefined>(undefined)
  const [createOpen, setCreateOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [detail, setDetail] = useState<any>(null)
  const [auditOpen, setAuditOpen] = useState(false)
  const [auditRecord, setAuditRecord] = useState<any>(null)
  const [submitting, setSubmitting] = useState(false)
  const [form] = Form.useForm()
  const [auditForm] = Form.useForm()
  const [suppliers, setSuppliers] = useState<any[]>([])
  const [purchaseOrders, setPurchaseOrders] = useState<any[]>([])
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(null)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [uploadFileList, setUploadFileList] = useState<UploadFile[]>([])
  const fileInputRef = useRef<any>(null)

  useEffect(() => { loadData(); loadSuppliers() }, [page, statusFilter])

  const loadData = async () => {
    setLoading(true)
    try {
      const params: any = { page, page_size: 20 }
      if (statusFilter !== undefined) params.status = statusFilter
      const res: any = await purchaseInvoiceApi.getList(params)
      setList(res.data?.list || [])
      setTotal(res.data?.total || 0)
    } catch { message.error('加载失败') }
    finally { setLoading(false) }
  }

  const loadSuppliers = async () => {
    try {
      const res: any = await purchaseInvoiceApi.getSuppliers()
      setSuppliers(res.data || [])
    } catch { /* ignore */ }
  }

  const loadPurchaseOrders = async (supplierId: number) => {
    setSelectedSupplierId(supplierId)
    const supplier = suppliers.find(s => s.id === supplierId)
    if (supplier) {
      form.setFieldsValue({ supplier_name: supplier.name })
    }
    try {
      const res: any = await purchaseInvoiceApi.getPurchaseOrders(supplierId)
      setPurchaseOrders(res.data || [])
    } catch { setPurchaseOrders([]) }
  }

  const handleSupplierChange = (v: number | undefined) => {
    if (v) {
      loadPurchaseOrders(v)
    } else {
      setSelectedSupplierId(null)
      setPurchaseOrders([])
      form.setFieldsValue({ supplier_name: '' })
    }
  }

  const openCreate = () => {
    form.resetFields()
    setSelectedSupplierId(null)
    setPurchaseOrders([])
    setUploadFileList([])
    setCreateOpen(true)
  }

  const openDetail = async (id: number) => {
    try {
      const res: any = await purchaseInvoiceApi.getDetail(id)
      setDetail(res.data)
      setDetailOpen(true)
    } catch { message.error('获取详情失败') }
  }

  const openAudit = (record: any) => {
    setAuditRecord(record)
    auditForm.resetFields()
    setAuditOpen(true)
  }

  const handleCreate = async () => {
    try {
      const values = await form.validateFields()
      setSubmitting(true)
      if (values.items) {
        values.items = values.items.filter((it: any) => it.purchase_order_id || it.amount)
      }
      // 带上文件路径
      if (uploadFileList.length > 0 && uploadFileList[0].response?.data?.file_path) {
        values.file_path = uploadFileList[0].response.data.file_path
      }
      await purchaseInvoiceApi.create(values)
      message.success('创建成功')
      setCreateOpen(false)
      loadData()
    } catch (err: any) {
      if (!err.message?.includes('validate')) message.error(err?.data?.message || '创建失败')
    } finally { setSubmitting(false) }
  }

  const handleAudit = async () => {
    try {
      const values = await auditForm.validateFields()
      setSubmitting(true)
      await purchaseInvoiceApi.audit(auditRecord.id, values)
      message.success('处理成功')
      setAuditOpen(false)
      loadData()
    } catch (err: any) {
      if (!err.message?.includes('validate')) message.error(err?.data?.message || '操作失败')
    } finally { setSubmitting(false) }
  }

  const handleDelete = async (id: number) => {
    try {
      await purchaseInvoiceApi.delete(id)
      message.success('删除成功')
      loadData()
    } catch { message.error('删除失败') }
  }

  const uploadProps = { disabled: true }; // placeholder
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const isValid = file.type === 'application/pdf' || ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)
    if (!isValid) { message.error('只支持 PDF/JPG/PNG 格式'); return }
    if (file.size / 1024 / 1024 > 10) { message.error('文件不能超过 10MB'); return }
    setUploadLoading(true)
    try {
      const form = new FormData()
      form.append('file', file)
      const res: any = await axios.post('/api/purchase-invoices/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
      })
      if (res.data.code === 200) {
        setUploadFileList([{ uid: '-1', name: res.data.data.filename, status: 'done', response: res.data } as UploadFile])
        message.success('上传成功')
      } else {
        message.error(res.data.message || '上传失败')
        setUploadFileList([])
      }
    } catch { message.error('上传失败'); setUploadFileList([]) }
    finally { setUploadLoading(false); if (fileInputRef.current) fileInputRef.current.value = '' }
  }

  const columns = [
    { title: '发票编号', dataIndex: 'invoice_no', width: 180 },
    { title: '供应商', dataIndex: 'supplier_name', width: 140, render: (v: string, r: any) => v || r.supplier?.name },
    { title: '开票日期', dataIndex: 'invoice_date', width: 120 },
    { title: '价税合计', dataIndex: 'total_amount', width: 120, render: (v: number) => <span style={{ fontWeight: 600 }}>¥{Number(v).toFixed(2)}</span> },
    { title: '税额', dataIndex: 'tax_amount', width: 100, render: (v: number) => `¥${Number(v).toFixed(2)}` },
    { title: '税率', dataIndex: 'tax_rate', width: 80, render: (v: number) => v ? `${v}%` : '-' },
    { title: '状态', dataIndex: 'status', width: 100, render: (v: number) => { const s = statusMap[v]; return s ? <Tag color={s.color}>{s.label}</Tag> : v } },
    { title: '创建时间', dataIndex: 'created_at', width: 170, render: (v: string) => new Date(v).toLocaleString() },
    {
      title: '操作', width: 200, fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" onClick={() => openDetail(record.id)}>详情</Button>
          {record.status === 1 && (
            <>
              <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => { auditForm.setFieldsValue({ status: 2 }); openAudit(record) }}>通过</Button>
              <Button size="small" danger icon={<CloseOutlined />} onClick={() => { auditForm.setFieldsValue({ status: 3 }); openAudit(record) }}>拒绝</Button>
              <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)}>
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </>
          )}
        </Space>
      )
    }
  ]

  const renderLinkedOrders = () => {
    if (!selectedSupplierId) return null
    if (purchaseOrders.length > 0) {
      return (
        <Form.List name="items">
          {(fields, { add, remove }) => (
            <div>
              <div style={{ marginBottom: 8, fontWeight: 600 }}>关联采购单（可选）</div>
              {fields.map(({ key, name, ...restField }) => (
                <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                  <Form.Item {...restField} name={[name, 'purchase_order_id']}>
                    <Select placeholder="选择采购单" style={{ width: 250 }} allowClear
                      onChange={(v) => {
                        if (v) {
                          const po = purchaseOrders.find(p => p.id === v)
                          if (po) form.setFieldValue(['items', name, 'amount'], Number(po.final_amount || 0))
                        } else {
                          form.setFieldValue(['items', name, 'amount'], undefined)
                        }
                      }}>
                      {purchaseOrders.map(po => (
                        <Select.Option key={po.id} value={po.id}>
                          {po.order_no} ¥{Number(po.final_amount || 0).toFixed(2)}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item {...restField} name={[name, 'amount']}>
                    <InputNumber placeholder="金额" style={{ width: 120 }} precision={2} min={0} />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, 'remark']}>
                    <Input placeholder="备注" style={{ width: 120 }} />
                  </Form.Item>
                  <DeleteOutlined onClick={() => remove(name)} style={{ color: '#999', cursor: 'pointer' }} />
                </Space>
              ))}
              <Button type="dashed" onClick={() => add()} block>+ 添加采购单</Button>
            </div>
          )}
        </Form.List>
      )
    }
    return (
      <div style={{ color: '#999', fontSize: 13, marginTop: 8 }}>
        该供应商暂无可关联的采购单，可直接填写金额创建发票
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>采购发票管理</h2>
        <Space>
          <Select placeholder="筛选状态" allowClear style={{ width: 120 }}
            onChange={(v) => { setStatusFilter(v); setPage(1) }}
            value={statusFilter}>
            {Object.entries(statusMap).map(([k, v]) => (
              <Select.Option key={k} value={parseInt(k)}>{v.label}</Select.Option>
            ))}
          </Select>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新建采购发票</Button>
        </Space>
      </div>

      <Card>
        <Table dataSource={list} columns={columns} rowKey="id" loading={loading}
          scroll={{ x: 1100 }}
          pagination={{ current: page, pageSize: 20, total, onChange: setPage }} />
      </Card>

      <Modal title="新建采购发票" open={createOpen} onCancel={() => setCreateOpen(false)}
        onOk={handleCreate} confirmLoading={submitting} okText="创建" width={700} destroyOnClose>
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Space style={{ width: '100%' }} size="large">
            <Form.Item name="supplier_id" label="选择供应商" style={{ minWidth: 200 }}>
              <Select placeholder="请选择（临时供应商可跳过）" showSearch optionFilterProp="children" allowClear
                onChange={handleSupplierChange}>
                {suppliers.map(s => (
                  <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="supplier_name" label="供应商名称" rules={[{ required: true, message: '请填写供应商名称' }]} style={{ minWidth: 200 }}>
              <Input placeholder="选择供应商自动填入，临时供应商手动输入" />
            </Form.Item>
          </Space>
          <Space style={{ width: '100%' }} size="large">
            <Form.Item name="invoice_date" label="开票日期" style={{ minWidth: 200 }}>
              <Input type="date" />
            </Form.Item>
            <Form.Item name="total_amount" label="价税合计（元）" rules={[{ required: true, message: '请填写金额' }]} style={{ minWidth: 200 }}>
              <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="0.00" />
            </Form.Item>
          </Space>
          <Space style={{ width: '100%' }} size="large">
            <Form.Item name="tax_amount" label="税额（元）" style={{ minWidth: 200 }}>
              <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="0.00" />
            </Form.Item>
            <Form.Item name="tax_rate" label="税率（%）" style={{ minWidth: 200 }}>
              <InputNumber style={{ width: '100%' }} min={0} max={99} precision={1} placeholder="如 13" />
            </Form.Item>
          </Space>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} placeholder="可选" />
          </Form.Item>
          <Form.Item label="上传发票（仅供查看下载）">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Button icon={<UploadOutlined />} loading={uploadLoading}
                onClick={() => fileInputRef.current?.click()}>
                {uploadFileList.length > 0 ? '已选择: ' + uploadFileList[0].name : '上传发票文件'}
              </Button>
              <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileSelect} />
            </div>
            <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
              支持 PDF/JPG/PNG，大小不超过10MB，仅供查看和下载
            </div>
          </Form.Item>
          {renderLinkedOrders()}
        </Form>
      </Modal>

      <Modal title="采购发票详情" open={detailOpen} onCancel={() => setDetailOpen(false)} footer={null} width={600}>
        {detail && (
          <div style={{ lineHeight: 2.2 }}>
            <p><strong>发票编号：</strong>{detail.invoice_no}</p>
            <p><strong>供应商：</strong>{detail.supplier_name || detail.supplier?.name}</p>
            <p><strong>开票日期：</strong>{detail.invoice_date}</p>
            <p><strong>价税合计：</strong><span style={{ color: '#ff4d4f', fontWeight: 700, fontSize: 16 }}>¥{Number(detail.total_amount).toFixed(2)}</span></p>
            <p><strong>税额：</strong>¥{Number(detail.tax_amount).toFixed(2)}</p>
            <p><strong>税率：</strong>{detail.tax_rate}%</p>
            <p><strong>不含税金额：</strong>¥{(Number(detail.total_amount) - Number(detail.tax_amount)).toFixed(2)}</p>
            <p><strong>状态：</strong><Tag color={statusMap[detail.status]?.color}>{statusMap[detail.status]?.label}</Tag></p>
            {detail.remark && <p><strong>备注：</strong>{detail.remark}</p>}
            {detail.audit_remark && <p><strong>审核备注：</strong>{detail.audit_remark}</p>}
            <p><strong>创建时间：</strong>{new Date(detail.created_at).toLocaleString()}</p>

            {detail.file_path && (
              <div style={{ marginTop: 16, padding: '12px 16px', background: '#f5f5f5', borderRadius: 6 }}>
                <Space>
                  <FileOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>发票文件</div>
                    <a href={purchaseInvoiceApi.getFileUrl(detail.file_path)} target="_blank" rel="noopener noreferrer">
                      <Button type="link" icon={<DownloadOutlined />} size="small">
                        查看/下载
                      </Button>
                    </a>
                  </div>
                </Space>
              </div>
            )}

            {detail.items && detail.items.length > 0 && (
              <>
                <div style={{ fontWeight: 600, marginTop: 16, marginBottom: 8 }}>关联采购单</div>
                <Table
                  dataSource={detail.items}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  columns={[
                    { title: '采购单号', dataIndex: 'purchase_order_no' },
                    { title: '金额', dataIndex: 'amount', render: (v: number) => `¥${Number(v).toFixed(2)}` },
                    { title: '备注', dataIndex: 'remark' }
                  ]}
                />
              </>
            )}
          </div>
        )}
      </Modal>

      <Modal title="审核采购发票" open={auditOpen} onCancel={() => setAuditOpen(false)}
        onOk={handleAudit} confirmLoading={submitting} okText="确认" destroyOnClose>
        <Form form={auditForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="status" label="审核结果" rules={[{ required: true }]}>
            <Select>
              <Select.Option value={2}>通过</Select.Option>
              <Select.Option value={3}>拒绝</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="audit_remark" label="审核备注">
            <Input.TextArea rows={3} placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
